import {
  isBoldGlyph,
  randomGlyph,
  BOLD_POOL,
  MEDIUM_POOL,
  THIN_POOL,
} from "../art/pastiradit";
import {
  ALPHA_MIN,
  ALPHA_MAX,
  SHADOW_ALPHA,
  FONT_STACK,
  LIGHT_X,
  LIGHT_Y,
  LIGHT_Z,
  SHADOW_DIR_X,
  SHADOW_DIR_Y,
  ink,
} from "./theme";

export type Globe = {
  opacity: number;
  update: (
    dt: number,
    pointerX: number,
    pointerY: number,
    cx: number,
    cy: number,
  ) => void;
  draw: (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    scale: number,
  ) => void;
};

const SHADOW_STEP = 1.5;
const FLICKER_MIN = 0.04;
const FLICKER_MAX = 0.14;
const FONT_MIN = 3;

/** Cell aspect 0.9:1.35 -> cols/rows = 1.5 keeps a circular silhouette. */
const GLOBE_COLS = 72;
const GLOBE_ROWS = 48;
const CELL_COUNT = GLOBE_COLS * GLOBE_ROWS;

const LERP = 0.1;
const SPIN = 0.18;
const MAX_TILT = 0.32;
const OCEAN_POOL = ".:'`";

/** Lat/lon ellipses (degrees) approximating continents. */
type LandBlob = readonly [lat: number, lon: number, latR: number, lonR: number];

const LAND: LandBlob[] = [
  [72, -42, 10, 18],
  [72, -96, 8, 28],
  [65, -150, 8, 22],
  [56, -100, 14, 36],
  [58, -70, 10, 18],
  [40, -98, 12, 28],
  [46, -110, 8, 16],
  [22, -102, 10, 14],
  [12, -86, 8, 10],
  [3, -70, 12, 16],
  [-8, -56, 16, 16],
  [-22, -64, 14, 12],
  [-40, -68, 14, 10],
  [54, -2, 5, 8],
  [50, 10, 12, 18],
  [62, 18, 8, 16],
  [54, 38, 8, 14],
  [45, 14, 7, 10],
  [40, -4, 6, 8],
  [8, 20, 28, 26],
  [22, 0, 12, 16],
  [-22, 24, 12, 14],
  [5, 8, 10, 10],
  [-19, 47, 8, 4],
  [28, 46, 10, 16],
  [22, 79, 14, 12],
  [60, 90, 12, 50],
  [58, 130, 10, 28],
  [50, 80, 10, 28],
  [32, 108, 14, 22],
  [28, 104, 10, 18],
  [15, 102, 8, 12],
  [4, 102, 6, 8],
  [37, 138, 8, 6],
  [0, 118, 6, 20],
  [-2, 138, 5, 12],
  [-24, 134, 14, 20],
  [-32, 148, 8, 10],
  [-42, 172, 8, 6],
];

type FlickerCell = {
  current: string;
  ttl: number;
  land: boolean;
  on: boolean;
  shade: number;
};

function nextTtl(rng = Math.random): number {
  return FLICKER_MIN + rng() * (FLICKER_MAX - FLICKER_MIN);
}

function wrapLon(lon: number): number {
  let x = lon;
  while (x < -180) x += 360;
  while (x > 180) x -= 360;
  return x;
}

function isLand(lat: number, lon: number): boolean {
  if (lat < -72) return true;
  if (lat < -64) {
    const w = Math.abs(Math.sin((lon * Math.PI) / 90));
    if (w > 0.12) return true;
  }
  const lonW = wrapLon(lon);
  const n =
    Math.sin(lat * 0.19 + lonW * 0.11) * 0.12 +
    Math.sin(lat * 0.37 - lonW * 0.23) * 0.08;
  for (let i = 0; i < LAND.length; i++) {
    const [clat, clon, latR, lonR] = LAND[i]!;
    let dlon = Math.abs(lonW - clon);
    if (dlon > 180) dlon = 360 - dlon;
    const u = dlon / lonR;
    const v = (lat - clat) / latR;
    if (u * u + v * v <= 1 + n) return true;
  }
  return false;
}

function hash01(col: number, row: number): number {
  const n = Math.sin(col * 12.9898 + row * 78.233) * 43758.5453;
  return n - Math.floor(n);
}

function pickLandGlyph(shade: number): string {
  if (shade > 0.62) return randomGlyph(BOLD_POOL);
  if (shade > 0.32) return randomGlyph(MEDIUM_POOL);
  return randomGlyph(THIN_POOL);
}

function pickOceanGlyph(shade: number): string {
  if (shade > 0.55 && Math.random() < 0.4) return ":";
  return randomGlyph(OCEAN_POOL);
}

function keepOcean(
  col: number,
  row: number,
  r2: number,
  shade: number,
): boolean {
  if (r2 > 0.76) return true;
  const floor = 0.46 + (1 - shade) * 0.22;
  return hash01(col, row) > floor;
}

type Sample = {
  land: boolean;
  shade: number;
  r2: number;
};

function sampleCell(
  col: number,
  row: number,
  yaw: number,
  pitch: number,
): Sample | null {
  const vx = ((col + 0.5) / GLOBE_COLS) * 2 - 1;
  const vy = -(((row + 0.5) / GLOBE_ROWS) * 2 - 1);
  const r2 = vx * vx + vy * vy;
  if (r2 >= 1) return null;
  const vz = Math.sqrt(1 - r2);

  const cy = Math.cos(pitch);
  const sy = Math.sin(pitch);
  const y1 = vy * cy + vz * sy;
  const z1 = -vy * sy + vz * cy;

  const cw = Math.cos(yaw);
  const sw = Math.sin(yaw);
  const mx = vx * cw - z1 * sw;
  const my = y1;
  const mz = vx * sw + z1 * cw;

  const lat = (Math.asin(Math.max(-1, Math.min(1, my))) * 180) / Math.PI;
  const lon = (Math.atan2(mx, mz) * 180) / Math.PI;
  const shade = Math.max(0, vx * LIGHT_X + vy * LIGHT_Y + vz * LIGHT_Z);

  return { land: isLand(lat, lon), shade, r2 };
}

export function createGlobe(): Globe {
  let opacity = 1;
  let time = 0;
  let yaw = -0.35;
  let pitch = 0.12;
  let yawOff = 0;
  let targetPitch = 0.12;
  let targetYawOff = 0;

  const cells: FlickerCell[] = Array.from({ length: CELL_COUNT }, () => ({
    current: ".",
    ttl: nextTtl(),
    land: false,
    on: false,
    shade: 0,
  }));

  const seedCell = (
    cell: FlickerCell,
    sample: Sample,
    col: number,
    row: number,
  ) => {
    if (!sample.land && !keepOcean(col, row, sample.r2, sample.shade)) {
      cell.on = false;
      cell.current = "";
      cell.land = false;
      cell.shade = sample.shade;
      return;
    }
    cell.on = true;
    cell.land = sample.land;
    cell.shade = sample.shade;
    cell.current = sample.land
      ? pickLandGlyph(sample.shade)
      : pickOceanGlyph(sample.shade);
    cell.ttl = nextTtl();
  };

  const update: Globe["update"] = (
    dt,
    pointerX,
    pointerY,
    cx,
    cy,
  ) => {
    time += dt;

    const dx = pointerX - cx;
    const dy = pointerY - cy;
    targetPitch = 0.12 - Math.max(-1, Math.min(1, dy / 320)) * MAX_TILT;
    targetYawOff = Math.max(-1, Math.min(1, dx / 320)) * MAX_TILT;
    yaw += dt * SPIN;

    const k = 1 - Math.pow(1 - LERP, dt * 60);
    pitch += (targetPitch - pitch) * k;
    yawOff += (targetYawOff - yawOff) * k;

    const totalYaw = yaw + yawOff;

    for (let row = 0; row < GLOBE_ROWS; row++) {
      for (let col = 0; col < GLOBE_COLS; col++) {
        const cell = cells[row * GLOBE_COLS + col]!;
        const sample = sampleCell(col, row, totalYaw, pitch);
        if (!sample) {
          cell.on = false;
          cell.current = "";
          continue;
        }

        cell.shade = sample.shade;
        const visible =
          sample.land || keepOcean(col, row, sample.r2, sample.shade);
        if (!visible) {
          cell.on = false;
          cell.current = "";
          cell.land = false;
          continue;
        }

        if (!cell.on || cell.land !== sample.land) {
          seedCell(cell, sample, col, row);
          continue;
        }

        cell.ttl -= dt;
        if (cell.ttl <= 0) {
          cell.current = sample.land
            ? pickLandGlyph(sample.shade)
            : pickOceanGlyph(sample.shade);
          cell.ttl = nextTtl();
        }
      }
    }
  };

  const draw: Globe["draw"] = (ctx, cx, cy, scale) => {
    if (opacity <= 0.001) return;

    const diameter = scale;
    const cellW = diameter / GLOBE_COLS;
    const cellH = diameter / GLOBE_ROWS;
    const fontSize = Math.max(FONT_MIN, Math.min(cellW, cellH) * 0.92);
    const shadowDx = SHADOW_DIR_X * SHADOW_STEP;
    const shadowDy = SHADOW_DIR_Y * SHADOW_STEP;

    const originX = cx - (GLOBE_COLS * cellW) / 2;
    const originY = cy - (GLOBE_ROWS * cellH) / 2;

    const breath = 0.5 + 0.5 * Math.sin(time * 0.55);
    const mainA = (ALPHA_MIN + (ALPHA_MAX - ALPHA_MIN) * breath) * opacity;
    const mediumFont = `500 ${fontSize}px ${FONT_STACK}`;
    const boldFont = `700 ${fontSize}px ${FONT_STACK}`;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.font = mediumFont;
    ctx.fillStyle = ink(SHADOW_ALPHA * opacity);
    for (let row = 0; row < GLOBE_ROWS; row++) {
      for (let col = 0; col < GLOBE_COLS; col++) {
        const cell = cells[row * GLOBE_COLS + col]!;
        if (!cell.on || !cell.current) continue;
        const x = originX + (col + 0.5) * cellW + shadowDx;
        const y = originY + (row + 0.5) * cellH + shadowDy;
        ctx.fillText((row + col) % 2 === 0 ? "." : ":", x, y);
      }
    }

    for (let row = 0; row < GLOBE_ROWS; row++) {
      for (let col = 0; col < GLOBE_COLS; col++) {
        const cell = cells[row * GLOBE_COLS + col]!;
        if (!cell.on || !cell.current) continue;
        const a = mainA * (0.52 + 0.48 * cell.shade);
        const x = originX + (col + 0.5) * cellW;
        const y = originY + (row + 0.5) * cellH;
        ctx.font = isBoldGlyph(cell.current) ? boldFont : mediumFont;
        ctx.fillStyle = ink(a);
        ctx.fillText(cell.current, x, y);
      }
    }

    ctx.restore();
  };

  return {
    get opacity() {
      return opacity;
    },
    set opacity(v: number) {
      opacity = Math.max(0, Math.min(1, v));
    },
    update,
    draw,
  };
}


